import type { ReactNode } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { ArrowLeft } from 'lucide-react'
import { Button } from '../components/ui/Button'
import { HomeDashboardHeaderActions } from '../components/layout/HomeDashboardHeaderActions'
import { useI18n } from '../i18n'
import styles from '@/styles/layouts/PageLayout.module.scss'

export interface PageLayoutProps {
  /** Stable root class for page-scoped CSS (e.g. `compliance-page`, `home-page`). */
  pageScope?: string
  /** Page title (left, top). */
  title: ReactNode
  /** Optional subtitle or meta line under the title. */
  description?: ReactNode
  /** Secondary toolbar (filters, KPI widgets, etc.) on its own row below the title and primary header action. */
  actions?: ReactNode
  /** Main page body below the header row. */
  children: ReactNode
  className?: string
  headerClassName?: string
  /** Wrapper around `children` (default: `PageLayout.module.scss` content block). */
  contentClassName?: string
  /** Hide header block entirely (title/back/actions). */
  hideHeader?: boolean
}

/**
 * Standard app shell: title row with primary header action (Run Scan + last scan on `/` and `/home`,
 * otherwise Back to Dashboard), optional description, optional secondary toolbar (`actions`), then content.
 */
export function PageLayout({
  pageScope = '',
  title,
  description,
  actions,
  children,
  className = '',
  headerClassName = '',
  contentClassName = styles.content,
  hideHeader = false,
}: PageLayoutProps) {
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const { t } = useI18n()
  const isHomeRoute = pathname === '/' || pathname === '/home'
  const isComplianceCenterRoute = pathname.startsWith('/compliance-center/')

  const rootClasses = [pageScope, styles.root, className].filter(Boolean).join(' ')
  return (
    <div className={rootClasses}>
      {!hideHeader ? (
        <header className={`${styles.header} ${headerClassName}`.trim()}>
          <div className={styles.titleRow}>
            <div className={styles.titleColumn}>
              <h1 className={styles.title}>{title}</h1>
            </div>
            <div className={styles.primaryActions}>
              {isHomeRoute ? (
                <HomeDashboardHeaderActions />
              ) : !isComplianceCenterRoute ? (
                <Button
                  type="button"
                  color="outline"
                  icon={<ArrowLeft className={styles.headerIcon} aria-hidden />}
                  onClick={() => navigate('/')}
                  className={styles.backButton}
                >
                  {t('reports.backToDashboard', 'Back to Dashboard')}
                </Button>
              ) : null}
            </div>
          </div>
          {description != null && description !== false && (
            <div className={styles.description}>{description}</div>
          )}
          {actions != null && actions !== false && (
            <div className={styles.actionsToolbar}>{actions}</div>
          )}
        </header>
      ) : null}
      <div className={contentClassName}>{children}</div>
    </div>
  )
}

export default PageLayout
